'use client';

import { Button, Spinner } from '@common/components';
import { SvgCs } from '@common/icon';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';

import { AuthInfiniteQueryOptions, UnauthInfiniteQueryOptions } from '@/api';
import { InquiryCard } from '@/app/donation/_components/InquiryCard';
import { INQUIRY_TYPE } from '@/app/donation/constants';
import type { InquiryType } from '@/app/donation/types';
import { BREAKPOINTS, DONATION_ROUTES, STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/AuthStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';

import { TABS } from '../../../constants';
import { ResponsiveDivider } from '../../GiftDetailTemplate/ResponsiveDivider';

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 20;

interface GiftTabContentInquiryProps {
  productNumber: string;
}

export const GiftTabContentInquiry = ({
  productNumber,
}: GiftTabContentInquiryProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const isLogin = useAuthStore((state) => state.isLogin);
  const router = useCustomRouter();

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    ...filledInfiniteQueryOptions(
      isLogin
        ? AuthInfiniteQueryOptions['get:/wello/v2/auth/home-inquiry']({
            query: {
              page: INITIAL_PAGE,
              size: INITIAL_SIZE,
              productNumber: productNumber,
            },
          })
        : UnauthInfiniteQueryOptions['get:/wello/v2/allows/home-inquiry']({
            query: {
              page: INITIAL_PAGE,
              size: INITIAL_SIZE,
              productNumber: productNumber,
            },
          }),
    ),
  });

  const { ref } = useInView({
    onChange: (inView) => {
      if (!isFetching && inView) fetchNextPage();
    },
  });

  const totalCount = data?.pages[0]?.context?.totalCount ?? 0;
  const isDisplayList = totalCount > 0;

  return (
    <section
      className={cn('flex flex-col pt-92', 'mobile-tablet:pt-52', {
        'pb-60': !isDisplayList,
      })}
      id={TABS.INQUIRY}
    >
      <div className="flex w-full flex-col items-center pb-28">
        <p
          className={cn(
            'mb-4 font-700 text-Gray-900 typo-body1',
            'mobile-tablet:typo-body3',
          )}
        >
          상품에 대해 궁금한 것이 있으신가요?
        </p>
        <p
          className={cn(
            'mb-32 font-400 text-Gray-600 typo-body2',
            'mobile-tablet:mb-20 mobile-tablet:typo-body4',
          )}
        >
          상품 관련 문의는 판매자가 상세히 답변드립니다.
        </p>
        <Button
          className={cn('w-[375px] gap-6', 'mobile-tablet:w-200')}
          theme="sub-line"
          dimensions={isDesktopViewport ? 'H56' : 'H48'}
          onClick={() => {
            sessionStorage.setItem(
              STORAGE_KEY.IS_DONATION_INQUIRY_BY_GIFT_PRODUCT,
              'true',
            );

            router.push(
              DONATION_ROUTES.MY_INQUIRY_POSTING.withSearchParams({
                dynamicPath: {
                  type: INQUIRY_TYPE.GIFT_PRODUCT,
                },
                searchParams: {
                  productNumber: productNumber,
                  redirect: window.location.pathname + window.location.search,
                },
              }),
            );
          }}
        >
          <SvgCs className="size-16 text-Gray-600" /> 답례품 문의하기
        </Button>
      </div>
      {isDisplayList ? (
        <section className="flex flex-col">
          <ResponsiveDivider />
          <section className="flex flex-col pt-16">
            <h2 className="px-20 pb-16 font-700 text-Gray-900 typo-body2">
              답례품 문의 {totalCount.toLocaleString()}
            </h2>
            <ul className="flex flex-col">
              {data?.pages
                .flatMap((page) => page.context?.contents ?? [])
                .map(
                  ({
                    homeInquiryId: home_inquiry_id,
                    inquiryTypeValue: inquiry_type_value,
                    contents,
                    createAt: create_at,
                    productName: product_name,
                    institutionName: institution_name,
                    institutionLogoUrl: institution_logo_url,
                    answer,
                    writerYN: writer_yn,
                    optionItems: option_items,
                    privateYN: private_yn,
                    title,
                    fileUrlList: file_url_list,
                    mainImageUrl: main_image_url,
                    inquiryTypeCd: inquiry_type_cd,
                  }) => (
                    <li key={home_inquiry_id}>
                      <InquiryCard
                        inquiryType={inquiry_type_value}
                        contents={contents}
                        createAt={create_at}
                        productName={product_name}
                        institutionName={institution_name}
                        institutionLogoUrl={institution_logo_url}
                        answer={answer}
                        writerYN={writer_yn}
                        optionItems={option_items}
                        privateYN={private_yn}
                        title={title}
                        fileUrlList={file_url_list}
                        mainImageUrl={main_image_url}
                        inquiryId={home_inquiry_id}
                        inquiryTypeCd={inquiry_type_cd as InquiryType}
                        className="border-b border-Gray-200"
                      />
                    </li>
                  ),
                )}
            </ul>
            {hasNextPage ? (
              <div className="my-30 w-full flex-center" ref={ref}>
                <Spinner />
              </div>
            ) : null}
          </section>
        </section>
      ) : null}
    </section>
  );
};
